import { createSlice } from '@reduxjs/toolkit';

const feedSlice = createSlice({
    name: 'feed',
    initialState: {
        items: [],
        mixes: [],
        isLoading: false,
        isLoadingMore: false,
        hasMore: true,
        error: null,
        lastFetchedAt: null,
        lastFetchedLikesTotal: null,  // null = unknown at fetch time (see feedThunk)
        lastFetchedFollowsTotal: null,
        timeMode: 1,            // -1=prefer old mixes, 0=random, 1=prefer new (default)
        cursor: null,           // opaque cursor for server pagination
        feedId: null,           // server-side feed cache ID
        subTab: 'tracks',       // 'tracks' | 'mixes' | 'trending' — persisted in nav history
        surpriseActive: false,  // true while the feed is showing a Surprise Me (random) roll
        discovery: 0,           // 0–1 share of the last feed that was random discovery (drives the personalize nudge)
        trendingItems: [],
        trendingIsLoading: false,
        trendingFetchedAt: null,
        trendingError: null,
        trendingWindow: null,   // { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
        // Free plan: main / music-info-server cut the lists to the first N and
        // report the rest as a count — { limit, shown, total, moreCount,
        // mixes?: { shown, total, moreCount } }. null = uncut (GOAT / open model).
        freeLimit: null,
        trendingFreeLimit: null,
        // Shown once when a free user's refresh / Surprise Me came back with
        // the same daily taste (freeLimit.taste === 'cached').
        tasteNotice: null,
        // GOAT user, but the server could not confirm the plan and cut the list
        // to the free five. Per list; stays until a later load of that list comes
        // back uncut (it used to be a 9 s one-liner that the other list's load blanked).
        goatCut: { feed: false, trending: false },
    },
    reducers: {
        setSubTab: (state, action) => {
            state.subTab = action.payload;
        },
        setTrendingItems: (state, action) => {
            state.trendingItems = action.payload;
        },
        setTrendingLoading: (state, action) => {
            state.trendingIsLoading = action.payload;
        },
        setTrendingFetchedAt: (state, action) => {
            state.trendingFetchedAt = action.payload;
        },
        setTrendingError: (state, action) => {
            state.trendingError = action.payload;
        },
        setTrendingWindow: (state, action) => {
            state.trendingWindow = action.payload;
        },
        setFreeLimit: (state, action) => {
            state.freeLimit = action.payload || null;
        },
        setTrendingFreeLimit: (state, action) => {
            state.trendingFreeLimit = action.payload || null;
        },
        setTasteNotice: (state, action) => {
            state.tasteNotice = action.payload || null;
        },
        setGoatCut: (state, action) => {
            const { list, value } = action.payload || {};
            if (list !== 'feed' && list !== 'trending') return;
            state.goatCut = { ...(state.goatCut || {}), [list]: Boolean(value) };
        },
        setItems: (state, action) => {
            state.items = action.payload;
        },
        setMixes: (state, action) => {
            state.mixes = action.payload;
        },
        appendItems: (state, action) => {
            state.items = [...state.items, ...action.payload];
        },
        interleaveAiPicks: (state, action) => {
            // Sprinkle "Picked for you" rows through the existing feed instead
            // of appending — otherwise they land at the bottom and stay invisible
            // until the user scrolls all the way down. Capped so they don't take
            // over the feed when the server returns hundreds.
            const MAX_INTERLEAVED = 15;
            const picks = (action.payload || []).slice(0, MAX_INTERLEAVED);
            if (!picks.length) return;
            if (!state.items.length) {
                state.items = picks;
                return;
            }
            const items = state.items;
            const stride = Math.max(4, Math.floor(items.length / picks.length));
            const result = [];
            let pickIdx = 0;
            for (let i = 0; i < items.length; i++) {
                if (pickIdx < picks.length && i >= 3 && (i - 3) % stride === 0) {
                    result.push(picks[pickIdx++]);
                }
                result.push(items[i]);
            }
            while (pickIdx < picks.length) result.push(picks[pickIdx++]);
            state.items = result;
        },
        setHasMore: (state, action) => {
            state.hasMore = action.payload;
        },
        setLoadingMore: (state, action) => {
            state.isLoadingMore = action.payload;
        },
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setLastFetchedAt: (state, action) => {
            state.lastFetchedAt = action.payload;
        },
        setLastFetchedTotals: (state, action) => {
            state.lastFetchedLikesTotal = action.payload.likes;
            state.lastFetchedFollowsTotal = action.payload.follows;
        },
        setTimeMode: (state, action) => {
            state.timeMode = action.payload;
            // Moving the slider returns to personalized — leave surprise mode.
            state.surpriseActive = false;
        },
        setSurpriseActive: (state, action) => {
            state.surpriseActive = action.payload;
        },
        setDiscovery: (state, action) => {
            state.discovery = action.payload;
        },
        setCursor: (state, action) => {
            state.cursor = action.payload;
        },
        setFeedId: (state, action) => {
            state.feedId = action.payload;
        },
        clearFeed: (state) => {
            state.items = [];
            state.mixes = [];
            state.error = null;
            state.lastFetchedAt = null;
            state.lastFetchedLikesTotal = null;
            state.lastFetchedFollowsTotal = null;
            state.hasMore = true;
            state.cursor = null;
            state.feedId = null;
            state.freeLimit = null;
            state.goatCut = { feed: false, trending: false };
        },
    },
});

export const {
    setItems,
    setMixes,
    appendItems,
    setFreeLimit,
    setTrendingFreeLimit,
    setTasteNotice,
    setGoatCut,
    interleaveAiPicks,
    setLoading,
    setLoadingMore,
    setError,
    setLastFetchedAt,
    setLastFetchedTotals,
    setTimeMode,
    setHasMore,
    setCursor,
    setFeedId,
    clearFeed,
    setSubTab,
    setSurpriseActive,
    setDiscovery,
    setTrendingItems,
    setTrendingLoading,
    setTrendingFetchedAt,
    setTrendingError,
    setTrendingWindow,
} = feedSlice.actions;

export default feedSlice.reducer;
